import { useVersionManager, ManagedStatus, checkAppVersion, VersionCheckAction } from '@magicdoorinc/avm';
import { confirm, useLocalization } from '@magicdoorinc/ui';
import { createEffect, createMemo, on } from 'solid-js';
import { isDev } from 'solid-js/web';
import { versionRepository } from '~/repositories/versionRepository';
import { App, VersionStatus } from '~/swagger/portal/Api';
import type { ManagedVersionInfo } from '@magicdoorinc/avm';

export const hookVersionInfo = () => {
  const { t } = useLocalization();
  const fetcher = async (): Promise<ManagedVersionInfo> => {
    if (isDev) {
      return {
        appVersion: '',
        status: ManagedStatus.Ok,
      };
    }

    const result = await versionRepository.getLatestVersionSafe('web');

    if (!result.success) {
      return {
        appVersion: '',
        status: ManagedStatus.Maintenance,
      };
    }

    const currentApp = result.versions.find((app) => app.app === App.CompanyApp);

    const status = currentApp?.status === VersionStatus.Maintenance ? ManagedStatus.Maintenance : ManagedStatus.Ok;
    return {
      appVersion: currentApp?.appVersion || '',
      status,
    };
  };

  const { versionInfo } = useVersionManager({ versionFetcher: fetcher });

  let alerted = false;

  createEffect(
    on(
      () => versionInfo.appVersion,
      (minAvailableVersion) => {
        const currentVersion = document.querySelector('meta[name="build-version"]')?.getAttribute('content');

        if (!currentVersion || !minAvailableVersion) return;

        const nextAction = checkAppVersion(currentVersion, minAvailableVersion);

        if (nextAction === VersionCheckAction.ForceRefresh) {
          return window.location.reload();
        }

        if (nextAction === VersionCheckAction.Notify) {
          if (alerted) return;
          alerted = true;
          return confirm({
            title: t('Update Required'),
            content: t('Please update to the latest version to continue.'),
            onResolve: (val) => {
              if (val) {
                window.location.reload();
              }
            },
          });
        }
      }
    )
  );

  const shouldShowMaintenance = createMemo(() => versionInfo.status === ManagedStatus.Maintenance);

  return {
    versionInfo,
    shouldShowMaintenance,
  };
};
